import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Alert, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';

import PageTitle from '../Layouts/PageTitle';
import authService from '../../services/auth.service';

interface ResetPasswordFormValues {
  tempPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onFinish = async (values: ResetPasswordFormValues) => {
    if (values.newPassword !== values.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      setLoading(false);
      return;
    }

    try {
      await authService.completePasswordReset(token, values.tempPassword, values.newPassword);

      message.success('Password successfully reset. You can now log in with your new password.');
      navigate('/sign-in');
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return <Alert title="Invalid reset link. Please request a new password reset." type="error" />;
  }

  return (
    <PageTitle title="Reset Password">
      <Card title="Reset Your Password" style={{ maxWidth: 400, width: '100%', margin: '10px' }}>
        {error && <Alert title={error} type="error" style={{ marginBottom: 16 }} />}

        <Form name="reset_password" onFinish={onFinish} layout="vertical">
          <Form.Item
            name="tempPassword"
            label="Temporary Password"
            rules={[{ required: true, message: 'Please enter your temporary password' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Temporary Password" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="New Password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm Password" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} style={{ width: '100%' }}>
              Reset Password
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </PageTitle>
  );
};

export default ResetPassword;
